import { useMemo } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { IoClose } from "react-icons/io5";

import { cn } from "@/lib/utils";
import { useTracksControllerListClassifications } from "@/lib/lutely-api/gen/tracks/tracks";
import { TracksControllerListClassificationsTypesItem } from "@/lib/lutely-api/gen/dtos/tracksControllerListClassificationsTypesItem";
import { MarketplaceSearch, MarketplaceFilterKey } from "./types";
import { XCircleIcon } from "@/assets/icons";

const MULTI_VALUE_KEYS = new Set<MarketplaceFilterKey>(["genre", "mood"]);

const valueFormatters: Record<string, (value: string) => string> = {
  bpm: (value) => value.replace("-", " – ") + " BPM",
  price: (value) => {
    if (value === "0-0") return "Free only";
    const [min, max] = value.split("-");
    return `$${min} - $${max}`;
  },
  rights: (value) =>
    value === "exclusive"
      ? "Exclusive only"
      : value?.replace(/^./, (c) => c.toUpperCase()) || "",
};

type FilterBarProps = {
  searchParameters: MarketplaceSearch;
  onRemoveFilter: (param: MarketplaceFilterKey) => void;
  className?: string;
};

export default function FilterBar({
  searchParameters,
  onRemoveFilter,
  className,
}: FilterBarProps) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const { data: classifications } = useTracksControllerListClassifications({
    types: [...Object.values(TracksControllerListClassificationsTypesItem)],
  });

  const entries = useMemo(() => {
    const chips: {
      key: MarketplaceFilterKey;
      value: string;
      label?: string;
    }[] = [];
    (
      Object.entries(searchParameters) as [
        MarketplaceFilterKey,
        string | undefined,
      ][]
    ).forEach(([key, raw]) => {
      if (!raw) {
        return;
      }

      if (MULTI_VALUE_KEYS.has(key)) {
        raw.split(", ").forEach((token) => {
          if (token) {
            const classification = classifications?.find(
              (c) => c.classificationId === token,
            );
            const label = classification ? classification.name : token;
            chips.push({ key, value: token, label });
          }
        });
      } else {
        const formatter = valueFormatters[key];
        const label = formatter ? formatter(raw) : raw;
        chips.push({ key, value: raw, label });
      }
    });
    return chips;
  }, [searchParameters, classifications]);

  if (entries.length === 0) {
    return null;
  }

  const handleClearAll = () =>
    navigate({
      to: pathname,
      search: () => ({}),
    });

  return (
    <div
      className={cn(
        "flex items-center gap-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      <div className="flex gap-2">
        {entries.map(({ key, value, label }, index) => (
          <button
            key={`${value}-${index}`}
            onClick={() => onRemoveFilter(key)}
            className="group flex h-6 items-center gap-1 rounded-full border border-black/10 bg-[#ECEFF4] px-2 text-[12px] font-semibold text-blue-900 whitespace-nowrap"
            aria-label={`Remove ${label || value} filter`}
          >
            <span>{label || value}</span>
            <IoClose className="text-[#525966] text-[20px] group-hover:scale-110" />
          </button>
        ))}
      </div>
      <button
        onClick={handleClearAll}
        className="text-[12px] flex items-center font-semibold text-[#525966] whitespace-nowrap rounded-lg border border-black/10 bg-white px-2 py-1 transition-colors hover:text-[#050C2B] hover:border-black/20 [&_svg]:transition-colors hover:[&_svg]:fill-[#050C2B]"
        aria-label="Clear all filters"
      >
        <XCircleIcon className="inline text-[16px] fill-[#525966] mr-[2px]" />
        Clear All
      </button>
      <div className="w-8 shrink-0" />
    </div>
  );
}
